  import React, { useState } from 'react';
  import {
    Row, Col,
    Card,
    Table,
    Badge,
    Button,
    Form,
    Pagination,
  } from 'react-bootstrap';
  import { Link } from 'react-router-dom';
  import { FiEye, FiCheck, FiTruck, FiPause, FiPlayCircle } from 'react-icons/fi';
  import { BsXCircle, BsPencilSquare } from "react-icons/bs";

  import axios from 'axios';
  import config from '../../config';
  import { toast } from 'react-toastify';

  import StockBreakdownModal from '../operationalDashboard/StockBreakdownModal';
  import TemporaryAllocationModal from '../operationalDashboard/TemporaryAllocationModal';
  import ConfirmationModal from '../common/ConfirmationModal';

  const SalesOrdersTab = ({
    salesOrders,
    soTabFilter,
    setSoTabFilter,
    stockSummary,
    reservedStock,
    getSOStatusBadge,
    getActionButtons,
    handleCheckBOM,
    handleRunningProduction,
    fetchDashboardData
  }) => {

    // State for stock breakdown modal
    const [showStockModal, setShowStockModal] = useState(false);
    const [selectedStockItem, setSelectedStockItem] = useState(null);
    const [searchFilter, setSearchFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(10);
    const [submitting, setSubmitting] = useState(false);

    // Modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
      title: '',
      message: '',
      confirmVariant: 'primary',
      confirmText: 'Confirm',
      onConfirm: () => { }
    });

    const openConfirm = (config) => {
      setConfirmConfig(config);
      setShowConfirmModal(true);
    };

    // Handler to show stock breakdown
    const [clickedItemSo, setClickedItemSo] = useState(null);
    // const [selectedStockItemSelectedStockItem] = useState(null);

    const [showAllocationModal, setShowAllocationModal] = useState(false);
    // const [allocationData, setAllocationData] = useState({});
    // eslint-disable-next-line
    const [allocationRefreshTrigger, setAllocationRefreshTrigger] = useState(0);

    // const [autoAllocating, setAutoAllocating] = useState({});

    const [showAddress, setShowAddress] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState("");

    const handleShowStockBreakdown = (item) => {
      setSelectedStockItem(item);
      setShowStockModal(true);
    };


    const handleShowAllocationModal = (item, so) => {
      // Enrich item with stock data from stockSummary
      const productStock = stockSummary.find(s => s.product._id === item.product._id);
      const enrichedItem = {
        ...item,
        product: {
          ...item.product,
          totalAvailableStock: productStock?.totalAvailableStock || 0
        }
      };
      setSelectedStockItem(enrichedItem);
      setClickedItemSo(so);
      setShowAllocationModal(true);
    };

    // Handler for when allocation is saved
    const handleAllocationSaved = () => {
      fetchDashboardData();
      setAllocationRefreshTrigger(prev => prev + 1);
    };

    // Calculate packing material requirement
    const getPackingRequirement = (item) => {
      if (!item.packingBagType) {
        return <span className="text-muted small">Not specified</span>;
      }

      const packingMaterial = item.packingBagType;
      const stuffing = packingMaterial.itemWiseStuffing?.find(
        s => s.product?._id === item.product?._id || s.product === item.product?._id
      );

      if (!stuffing || !stuffing.quantity) {
        return <span className="text-warning small">Not configured</span>;
      }

      const quantityPerBag = stuffing.quantity;
      const numberOfBags = Math.ceil(item.quantity / quantityPerBag);

      return (
        <div>
          {packingMaterial.name}
          <div className="small text-muted">
            {numberOfBags} bags ({quantityPerBag} kg/bag)
          </div>
        </div>
      );
    };

    const handleStatusChange = async (soId, newStatus) => {
      setSubmitting(true);
      try {
        await axios.patch(`${config.API_BASE_URL}/sales-orders/${soId}/status`, { status: newStatus });
        toast.success(`Sales Order status updated to ${newStatus}`);
        fetchDashboardData();
      } catch (error) {
        console.error('Error updating status:', error);
        toast.error('Failed to update status');
      } finally {
        setSubmitting(false);
        setShowConfirmModal(false);
      }
    };

    const handleApproveSO = async (soId) => {
      setSubmitting(true);
      try {
        await axios.patch(`${config.API_BASE_URL}/sales-orders/${soId}/approve`);
        toast.success("Sales Order approved successfully");
        fetchDashboardData();
      } catch (error) {
        console.error('Error approving SO:', error);
        toast.error('Failed to approve Sales Order');
      } finally {
        setSubmitting(false);
        setShowConfirmModal(false);
      }
    };

    const handleRejectSO = async (soId) => {
      setSubmitting(true);
      try {
        await axios.patch(`${config.API_BASE_URL}/sales-orders/${soId}/status`, { status: 'Cancelled' });
        toast.success("Sales Order rejected/cancelled");
        fetchDashboardData();
      } catch (error) {
        console.error('Error rejecting SO:', error);
        toast.error('Failed to reject Sales Order');
      } finally {
        setSubmitting(false);
        setShowConfirmModal(false);
      }
    };

    const getEnhancedActionButtons = (so) => {
      if (so.status === 'Draft') {
        return [
          <Button
            key="approve"
            variant="success"
            size="sm"
            className="d-flex align-items-center gap-1 text-nowrap"
            onClick={() => openConfirm({
              title: 'Approve Sales Order',
              message: `Are you sure you want to approve this draft Sales Order?`,
              confirmVariant: 'success',
              confirmText: 'Approve',
              onConfirm: () => handleApproveSO(so._id)
            })}
            title="Approve Draft SO"
          >
            <FiCheck /> Approve
          </Button>,
          <Link
            key="edit"
            to={`/edit-sales-order/${so._id}`}
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 text-nowrap"
            title="Edit Draft SO"
          >
            <BsPencilSquare /> Edit SO
          </Link>,
          <Button
            key="reject"
            variant="outline-danger"
            size="sm"
            className="d-flex align-items-center gap-1 text-nowrap"
            onClick={() => openConfirm({
              title: 'Reject Sales Order',
              message: 'Are you sure you want to reject/cancel this draft Sales Order?',
              confirmVariant: 'danger',
              confirmText: 'Reject Order',
              onConfirm: () => handleRejectSO(so._id)
            })}
            title="Reject/Cancel Draft SO"
          >
            <BsXCircle /> Reject/Cancel
          </Button>,
          <Link
            key="view"
            to={`/sales-orders/${so._id}`}
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 text-nowrap"
            title="View full order details"
          >
            <FiEye /> SO Details
          </Link>
        ];
      }

      // Determine per-item stock availability
      const anyItemMissing = so.items?.some(item => {
        const productStock = stockSummary.find(s => s.product._id === item.product._id);
        const availableStock = (productStock?.totalAvailableStock || 0);
        return availableStock < item.quantity;
      });
      const allItemsAvailable = so.items && so.items.length > 0 && !anyItemMissing;
      const buttons = [];

      // Hold / Unhold Button
      if (so.status === 'On Hold') {
        buttons.push(
          <Button
            key="unhold"
            variant="outline-success"
            size="sm"
            className="d-flex align-items-center gap-1 text-nowrap"
            onClick={() => openConfirm({
              title: 'Unhold Sales Order',
              message: 'Are you sure you want to resume this Sales Order?',
              confirmVariant: 'success',
              confirmText: 'Resume Order',
              onConfirm: () => handleStatusChange(so._id, 'Created')
            })}
            title="Resume order"
          >
            <FiPlayCircle /> Unhold SO
          </Button>
        );
      } else if (!['Delivered', 'Cancelled', 'Completed', 'Fully Dispatched'].includes(so.status)) {
        buttons.push(
          <Button
            key="hold"
            variant="outline-danger"
            size="sm"
            className="d-flex align-items-center gap-1 text-nowrap"
            onClick={() => openConfirm({
              title: 'Hold Sales Order',
              message: 'Are you sure you want to put this Sales Order on hold?',
              confirmVariant: 'warning',
              confirmText: 'Hold Order',
              onConfirm: () => handleStatusChange(so._id, 'On Hold')
            })}
            title="Put order on hold"
          >
            <FiPause /> Hold SO
          </Button>
        );
      }

      // Hide other functional buttons if on hold
      if (so.status === 'On Hold') {
        buttons.push(
          <Link
            key="view"
            to={`/sales-orders/${so._id}`}
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 text-nowrap"
            title="View full order details"
          >
            <FiEye /> SO Details
          </Link>
        );
        return buttons;
      }

      const hasAllocatedStock = so.items?.some(item => (item.allocatedQty || 0) > 0);

      if (so.status === 'Created' || so.status === 'Confirmed' || so.status === 'Finalized' || so.status === 'Filled From Production' || so.status === 'Partially Dispatched') {
        // Logic for Dispatch button: "active dispatch button when ever an SO have some allocated stock"
        if (hasAllocatedStock || allItemsAvailable) {
          // Show Dispatch button + View
          buttons.push(
            <Button
              key="dispatch"
              variant="success"
              size="sm"
              className="d-flex align-items-center gap-1 text-nowrap"
              onClick={() => {
                // Navigate to sales order details page with dispatch tab active
                window.location.href = `/sales-orders/${so._id}?tab=dispatch`;
              }}
              title="Ready for dispatch"
            >
              <FiTruck />Dispatch
            </Button>
          );
        }
      }

      // Quality Check button
      const isQCStatus = so.status === 'QC Pending' || so.status === 'Filled From Production & QC Pending';
      const labTestRequiredWithAllocation = so.labTestRequired && hasAllocatedStock;

      if (isQCStatus || labTestRequiredWithAllocation) {
        buttons.push(
          <Button
            key="qc"
            variant="warning"
            size="sm"
            className="d-flex align-items-center gap-1 text-nowrap"
            onClick={() => {
              // Navigate to sales order details page with QC tab active
              window.location.href = `/sales-orders/${so._id}?tab=qc-testing`;
            }}
            title="Go to Quality Check"
          >
            <FiCheck /> Quality Check
          </Button>
        );
      }

      buttons.push(
        <Link
          key="view"
          to={`/sales-orders/${so._id}`}
          className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 text-nowrap"
          title="View full order details"
        >
          <FiEye /> SO Details
        </Link>
      );

      return buttons;
    };

    const getFilteredOrders = (statusFilter) => {
      const allOrders = salesOrders;

      const searchTerm = searchFilter.toLowerCase();

      return allOrders.filter(so => {
        const statusMatch = statusFilter === 'all' || so.status === statusFilter;
        const searchMatch = searchTerm === '' ||
          so.orderNumber?.toLowerCase().includes(searchTerm) ||
          so.buyer?.business_name?.toLowerCase().includes(searchTerm) ||
          (so.items && so.items.some(item => item.product?.productName?.toLowerCase().includes(searchTerm)));
        return statusMatch && searchMatch;
      });
    };

    // Helper function to render individual item row
    const renderItemRow = (item, so, availableStock, requiredQuantity) => {
      // const itemKey = `${so._id}-${item._id}`;

      return (
        <>
          <td>
            <div><strong>{item.product?.productName}</strong></div>
            <small className="text-muted">{item.product?.productCode}</small>
          </td>
          <td>
            <div className="d-flex flex-column gap-1">
              <div className="d-flex justify-content-between align-items-center">
                <span className="small"><strong>Ordered:</strong></span>
                {item.quantity} {item.unit}
              </div>
              {so.status !== 'Draft' && (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small"><strong>Allocated:</strong></span>
                    <span
                      style={
                        {
                          cursor: 'pointer',
                        }
                      }
                      onClick={() => handleShowAllocationModal(item, so)}
                      title="Click to manage allocation"
                      className='border border-info rounded-2 px-2 bg-info-subtle'
                    >
                      {item.allocatedQty || 0} {item.unit}
                    </span>
                  </div>

                  {(item.dispatchedQty || 0) > 0 && (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small"><strong>Dispatched:</strong></span>
                      {item.dispatchedQty || 0} {item.unit}
                    </div>
                  )}
                </>
              )}

              <div className="d-flex justify-content-between align-items-center">
                <span className="small text-muted"><strong>Physical Stock:</strong></span>
                {availableStock} {item.unit}
              </div>

              {requiredQuantity > 0 && (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-danger"><strong>Shortage:</strong></span>
                  {requiredQuantity} {item.unit}
                </div>
              )}
            </div>
          </td>
          <td>
            {getPackingRequirement(item)}
          </td>
          <td>
            <div className="d-flex flex-column align-items-center gap-1">
              {availableStock >= item.quantity ? (
                <Badge bg="success" className="w-100 small">✓ In Stock</Badge>
              ) : availableStock > 0 ? (
                <Badge bg="warning" className="w-100 text-dark small">⚠ Low</Badge>
              ) : (
                <Badge bg="danger" className="w-100 small">✗ Out</Badge>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100 small text-nowrap"
                onClick={() => handleShowStockBreakdown(item)}
                title="View detailed stock breakdown"
              >
                View Stock Details
              </Button>
            </div>
          </td>
          <td>
            <div className="d-flex flex-column gap-1">
              {requiredQuantity > 0 &&
                !['On Hold', 'Draft', 'Cancelled', 'Completed', 'Fully Dispatched']
                  .includes(so.status) && (
                  <>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleCheckBOM(item, so._id)}
                      title="Check BOM"
                      style={{ flex: 1 }}
                      className="small d-flex gap-1 align-items-center text-nowrap"
                    >
                      <FiCheck size={12} /> Production Check & Raise PR
                    </Button>
                  </>
                )}
            </div>
          </td>
        </>
      );
    };

    // Universal table with all columns for all statuses
    const renderNewOrdersTable = (orders) => (
      <div className="table-responsive">
        <Table hover className="mb-0 small">
          <thead className="bg-dark fw-semibold">
            <tr>
              <th rowSpan="2" className="align-middle" style={{ minWidth: '120px' }}>Order #</th>
              <th rowSpan="2" className="align-middle" style={{ minWidth: '150px' }}>Customer</th>
              <th colSpan="5" className="text-center border-bottom-0">Items Details</th>
              <th rowSpan="2" className="align-middle">Actions</th>
            </tr>
            <tr>
              <th style={{ minWidth: '150px' }}>Product</th>
              <th style={{ minWidth: '200px' }}>Quantity Details</th>
              <th style={{ minWidth: '140px' }}>Packing</th>
              <th style={{ minWidth: '100px' }}>Stock</th>
              <th style={{ minWidth: '110px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  <div className="text-muted">No orders found</div>
                </td>
              </tr>
            ) : (
              orders.map((so) => {
                const rowSpan = Math.max(1, so.items?.length || 1);
                return (
                  <React.Fragment key={so._id}>
                    <tr>
                      <td rowSpan={rowSpan} className="align-middle border-end">
                        <strong className="text-primary">
                          <Link
                            key="view"
                            to={`/sales-orders/${so._id}`}
                            title="View full order details"
                          >
                            {so.orderNumber || <Badge bg="warning text-dark">DRAFT</Badge>}
                          </Link>
                        </strong>
                        <br />
                        <small className="text-muted">
                          EDD: <b className="text-muted">{new Date(so.EDD).toLocaleDateString()}</b>
                        </small><br />
                        {getSOStatusBadge(so.status)}
                      </td>

                      <td rowSpan={rowSpan} className="align-middle border-end">
                        <div>
                          <strong>{so.buyer?.business_name || 'N/A'}</strong>
                        </div>

                        {so.broker && (
                          <small className="text-muted d-block">
                            via {so.broker.business_name}
                          </small>
                        )}

                        <Link
                          className="small mt-1 text-decoration-underline"
                          onClick={() => {
                            setSelectedAddress(so || 'N/A');
                            setShowAddress(true);
                          }}
                        >
                          View Delivery Address
                        </Link>
                      </td>
                      {so.items && so.items.length > 0 ? (
                        (() => {
                          const item = so.items[0];
                          const productStock = stockSummary.find(s => s.product._id === item.product._id);
                          const availableStock = (productStock?.totalAvailableStock || 0);
                          const requiredQuantity = Math.max(0, item.quantity - availableStock);

                          return renderItemRow(item, so, availableStock, requiredQuantity);
                        })()
                      ) : (
                        <td colSpan="5" className="text-muted text-center">No items</td>
                      )}
                      <td rowSpan={rowSpan} className="align-middle border-start">
                        <div className="d-flex flex-column gap-1">
                          {getEnhancedActionButtons(so)}
                        </div>
                      </td>
                    </tr>
                    {so.items && so.items.slice(1).map((item, idx) => {
                      const productStock = stockSummary.find(s => s.product._id === item.product._id);
                      const availableStock = (productStock?.totalAvailableStock || 0);
                      const requiredQuantity = Math.max(0, item.quantity - availableStock);

                      return (
                        <tr key={`${so._id}-${idx + 1}`}>
                          {renderItemRow(item, so, availableStock, requiredQuantity)}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>

        {/* Stock Breakdown Modal */}
        <StockBreakdownModal
          show={showStockModal}
          onHide={() => setShowStockModal(false)}
          item={selectedStockItem}
          productId={selectedStockItem?.product?._id}
        />

        <TemporaryAllocationModal
          show={showAllocationModal}
          onHide={() => setShowAllocationModal(false)}
          item={selectedStockItem}
          so={clickedItemSo}
          onAllocationSaved={handleAllocationSaved}
        />

        <ConfirmationModal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          onConfirm={confirmConfig.onConfirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          confirmVariant={confirmConfig.confirmVariant}
          isLoading={submitting}
        />

        {showAddress && (
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Delivery Address for {selectedAddress.orderNumber || 'Draft SO'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddress(false)}
                  />
                </div>

                <div className="modal-body">
                  <pre className="mb-0 text-secondary">
                    {selectedAddress.buyerDeliveryDetails?.address || 'N/A'}
                  </pre>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowAddress(false)}
                  >
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    );

    // Get current filtered orders
    const currentOrders = getFilteredOrders(soTabFilter);

    // Pagination logic
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    const paginatedOrders = currentOrders.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalPages = Math.ceil(currentOrders.length / ordersPerPage);

    const getHeaderText = (currentOrders) => {
      if (soTabFilter === 'all' && !searchFilter) {
        return `All Sales Orders Overview (${currentOrders.length})`;
      }
      const statusText = soTabFilter === 'all' ? 'All Sales Orders' : `"${soTabFilter === 'Created' ? 'New' : soTabFilter}" Sales Orders`;
      if (searchFilter) {
        return `Search results for "${searchFilter}" in ${statusText}`;
      }
      return `${statusText} (${currentOrders.length})`;
    };

    const renderPagination = () => {
      if (totalPages <= 1) {
        return null;
      }

      const pageNumbers = [];
      const maxPagesToShow = 5;
      let startPage, endPage;

      if (totalPages <= maxPagesToShow) {
        startPage = 1;
        endPage = totalPages;
      } else {
        if (currentPage <= Math.ceil(maxPagesToShow / 2)) {
          startPage = 1;
          endPage = maxPagesToShow;
        } else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) {
          startPage = totalPages - maxPagesToShow + 1;
          endPage = totalPages;
        } else {
          startPage = currentPage - Math.floor(maxPagesToShow / 2);
          endPage = currentPage + Math.floor(maxPagesToShow / 2);
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      return (
        <Pagination>
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
          {startPage > 1 && <Pagination.Ellipsis onClick={() => setCurrentPage(startPage - 1)} />}
          {pageNumbers.map(number => (<Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>{number}</Pagination.Item>))}
          {endPage < totalPages && <Pagination.Ellipsis onClick={() => setCurrentPage(endPage + 1)} />}
          <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      );
    };

    return (
      <div className="sales-orders-tab">
        <Row className="mb-3">
          <Col md={4}>
            <Form.Control
              type="text"
              placeholder="Search by Order #, Customer, or Product"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Select value={soTabFilter} onChange={(e) => setSoTabFilter(e.target.value)}>
              <option value="all">All Sales Orders</option>
              <option value="Draft">Drafts</option>
              <option value="Created">New</option>
              <option value="PO Received">PO Received</option>
              <option value="Filled From Production">Filled From Production</option>
              <option value="Stock Pending">Stock Pending</option>
              <option value="Awaiting Receipt of PO">Awaiting Receipt of PO</option>
              <option value="Awaiting Receipt of PO (RM)">Awaiting Receipt of PO (RM)</option>
              <option value="Awaiting Receipt of PO (FG)">Awaiting Receipt of PO (FG)</option>
              <option value="In Production">In Production</option>
              <option value="Production Requested">Production Requested</option>
              <option value="Production Complete">Production Complete</option>
              <option value="QC Pending">QC Pending</option>
              <option value="Filled From Production & QC Pending">Filled From Production & QC Pending</option>
              <option value="Ready for Dispatch">Ready for Dispatch</option>
              <option value="Filled From Production & Ready for Dispatch">Filled From Production & Ready for Dispatch</option>
              <option value="Finalized">Finalized</option>
              <option value="Partially Dispatched">Partially Dispatched</option>
              <option value="Fully Dispatched">Fully Dispatched</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="On Hold">On Hold</option>
            </Form.Select>
          </Col>
        </Row>

        <Card>
          <Card.Header className="bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                {getHeaderText(currentOrders)}
              </h6>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {renderNewOrdersTable(paginatedOrders)}
          </Card.Body>
          {currentOrders.length > ordersPerPage && (
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, currentOrders.length)} of {currentOrders.length} orders
              </div>
              {renderPagination()}
            </Card.Footer>
          )}
        </Card>
      </div>
    );
  };

  export default SalesOrdersTab;
